import { HpSvgIconTypes } from '@avantia/style-guide';
import {
  getValidConvertibleInstanceAction,
  QUESTION_SET,
  TRANSACTION_TYPE
} from 'actions/getValidConvertibleInstanceAction';
import { openFeatureNotAvailableModal } from 'actions/openModalAction';
import { redirectToQuoteJourneyAction } from 'actions/redirectToQuoteJourneyAction';
import {
  analyticsUserType,
  GTM_EVENT_TYPES,
  MAKE_CHANGES_INTERACTION_VALUES,
  Q_INTERACTION_TYPES,
  sendAnalytics
} from 'analytics';
import { Title } from 'components/layout/header/title';
import { Button } from 'components/shared/button';
import { CardSelector } from 'components/shared/cardSelector';
import { HpChildComponentProps } from 'components/shared/componentWithModal/componentWithModal';
import { getConfig } from 'config/configuration';
import { PATH } from 'interfaces/pathTypes';
import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { shouldAllowCardUpdate } from 'utils';

import './makeChanges.scss';

interface CardWithAction {
  id: string;
  name: string;
  icon: HpSvgIconTypes;
  action: () => void;
}

export const MakeChanges = ({ selectedPolicy }: HpChildComponentProps): JSX.Element => {
  const { agentUpdatePolicyEnabled, editableRenewalEnabled, makeChangesEnabled } = getConfig();
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [error, setError] = useState<string>('');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const username = useAppSelector((state) => state.authentication.email);
  const isAgent = useAppSelector((state) => state.authentication.isAgent);

  const cardAnalyticsValue: Record<string, string> = {
    'update-policy': MAKE_CHANGES_INTERACTION_VALUES.UPDATE_POLICY,
    'update-auto-renewal': MAKE_CHANGES_INTERACTION_VALUES.AUTO_RENEWAL,
    'editable-renewal': MAKE_CHANGES_INTERACTION_VALUES.EDITABLE_RENEWAL,
    'update-card-details': MAKE_CHANGES_INTERACTION_VALUES.PAYMENT_DETAILS,
    'update-print-preference': MAKE_CHANGES_INTERACTION_VALUES.PRINT_PREFERENCE
  };

  const cards: Record<string, CardWithAction> = {
    'update-policy': {
      id: 'update-policy',
      name: 'Update policy',
      icon: 'file-pen' as HpSvgIconTypes,
      action: () => {
        if (!makeChangesEnabled || (isAgent && !agentUpdatePolicyEnabled)) {
          dispatch(openFeatureNotAvailableModal(isAgent));
          return;
        }

        dispatch(
          getValidConvertibleInstanceAction({
            username,
            transactionType: TRANSACTION_TYPE.REN,
            isAgent,
            questionSet: QUESTION_SET.CHANGE_RENEWAL_JOURNEY,
            onError: () => history.push(`${PATH.RENEWAL}/error`)
          })
        );
      }
    }
  };

  if (editableRenewalEnabled) {
    cards['editable-renewal'] = {
      id: 'editable-renewal',
      name: 'Fully Editable Renewal',
      icon: 'refresh' as HpSvgIconTypes,
      action: () => {
        const { policyDetailsId, historyId, policyNumber, email, correspondencePostcode } = selectedPolicy;
        dispatch(
          redirectToQuoteJourneyAction({
            policyDetailsId,
            historyId,
            policyNumber,
            callmediauser: username,
            emailAddress: email,
            postcode: correspondencePostcode
          })
        );
      }
    };
  }

  cards['update-auto-renewal'] = {
    id: 'update-auto-renewal',
    name: 'Update auto renewal',
    icon: 'calendar-pen' as HpSvgIconTypes,
    action: () => {
      history.push(`${PATH.SETTINGS}#auto-renewal-preferences`);
    }
  };

  cards['update-card-details'] = {
    id: 'update-card-details',
    name: 'Update card details',
    icon: 'credit-card' as HpSvgIconTypes,
    action: () => {
      history.push(PATH.PAYMENT_MANAGEMENT);
    }
  };

  cards['update-print-preference'] = {
    id: 'update-print-preference',
    name: 'Update print preference',
    icon: 'print' as HpSvgIconTypes,
    action: () => {
      history.push(`${PATH.SETTINGS}#print-preferences`);
    }
  };

  const handleContinue = (): void => {
    if (!selectedCard) {
      setError("You haven't selected an option. Choose one to continue.");
      return;
    }

    const selectedCardData = cards[selectedCard];
    if (selectedCardData) {
      sendAnalytics({
        event: GTM_EVENT_TYPES.QINTERACTION,
        detail: {
          qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
          qinteraction_locator: 'make_changes_option',
          qinteraction_value: cardAnalyticsValue[selectedCard],
          usertype: analyticsUserType(isAgent)
        }
      });
      selectedCardData.action();
    }
  };

  const handleCardSelection = (cardId: string): void => {
    setSelectedCard(cardId);
    setError('');
  };

  const cardVisibility: Record<string, boolean> = {
    'update-policy': true,
    'editable-renewal': true,
    'update-auto-renewal': true,
    'update-card-details': shouldAllowCardUpdate(selectedPolicy),
    'update-print-preference': isAgent
  };

  const visibleCards = Object.values(cards).filter((card) => cardVisibility[card.id]);

  return (
    <>
      <Title title="MAKE CHANGES" icon="pen-circle" id="make-changes" />
      <div className="make-change-container">
        <CardSelector
          cards={visibleCards}
          setSelectedCard={handleCardSelection}
          selectedCard={selectedCard}
          error={error}
        />
        <Button text="CONTINUE" variant="primary" handleClick={handleContinue} fullWidth />
        <Link to="/dashboard">
          <Button text="BACK" variant="reversed" fullWidth />
        </Link>
      </div>
    </>
  );
};
